import type { Filter, LiteIntegrationOut } from '@repo/api-codegen';
import { useApiGetBottomUpDatatree } from '@repo/api-codegen';
import { EntityType } from '@repo/common/enums/entityType';
import { useState } from 'react';
import { getEntityDisplayTitle } from '../../../lib/utils/entity';
import IntegrationLogo from '../../IntegrationLogo';
import type { CommonEntityTreeNode } from '../utils';
import { getEntityIconByType } from '../utils';
import type { PrefetchedEntityTreeNodeProps } from './BottomUpTreeEntityNode';
import { BottomUpTreeEntityNode } from './BottomUpTreeEntityNode';

const getIcon = (node: CommonEntityTreeNode) => {
	if (node.entity.entity_type === 'integration') {
		return (
			<IntegrationLogo
				integrationId={node.integration ? undefined : node.entity.id}
				integrationType={node.integration?.type}
				size={16}
			/>
		);
	}

	return getEntityIconByType(node.entity.entity_type);
};

const getLabel = (node: CommonEntityTreeNode) =>
	getEntityDisplayTitle(node.entity);

export function BottomUpTreeIntegrationNode({
	name,
	id,
	teamId,
	integration,
	onClick,
}: {
	name: string;
	id: string;
	teamId: string;
	integration?: LiteIntegrationOut;
	onClick: PrefetchedEntityTreeNodeProps['onClick'];
}) {
	const [open, setOpen] = useState(false);

	const { data: bottomUpDataTree } = useApiGetBottomUpDatatree(
		{
			queryParams: {
				team_id: teamId,
				filters: JSON.stringify({
					operator: 'and',
					operands: [
						{
							operator: 'exact',
							field: 'integration_id',
							value: id,
						},
						{
							operator: 'not',
							operands: [
								{
									operator: 'exact',
									field: 'entity_type',
									value: EntityType.metric,
								},
								{
									operator: 'exact',
									field: 'entity_type',
									value: EntityType.document,
								},
							],
						},
					],
				} as Filter),
			},
		},
		{
			retry: 1,
			select: (data) => Object.values(data ?? {}),
			enabled: open,
		}
	);

	return (
		<BottomUpTreeEntityNode
			key={id}
			node={{
				id,
				hasChildren: !bottomUpDataTree || bottomUpDataTree.length > 0,
				integration,
				entity: {
					id,
					entity_type: EntityType.integration,
					icon: '',
					title: name,
				},
			}}
			children={bottomUpDataTree}
			level={0}
			getLabel={getLabel}
			getIcon={getIcon}
			onClick={onClick}
			onOpen={setOpen}
		/>
	);
}
